import TagManager from 'react-gtm-module';

const LOCAL_GUID_KEY = 'local_gtm_guid';
let gtmInitialized = false;

function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

export function getOrCreateLocalGuid() {
  if (typeof window === 'undefined') return null;
  let guid = localStorage.getItem(LOCAL_GUID_KEY);
  if (!guid) {
    guid = generateUUID();
    localStorage.setItem(LOCAL_GUID_KEY, guid);
  }
  return guid;
}

/**
 * Initialize GTM once. Uses the GTM "set" command so local_guid
 * persists as a dataLayer variable available to ALL subsequent tags
 * (including data-gtm attribute triggers and custom events).
 */
export function initGtmOnce(config) {
  if (typeof window === 'undefined') return;
  if (gtmInitialized) return;

  const gtmId = config?.GTM_ID;
  if (!gtmId || gtmId === 'GTM-XXXXXX') return;

  TagManager.initialize({
    gtmId,
    dataLayerName: 'dataLayer',
  });

  // "set" makes these values persistent across all future dataLayer events,
  // so every tag (including data-gtm click triggers) can reference them.
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    'set': {
      local_guid: getOrCreateLocalGuid(),
    },
  });

  gtmInitialized = true;
}

export function firePageView(eventName, { locale, location }) {
  if (!gtmInitialized) return;
  TagManager.dataLayer({
    dataLayer: {
      event: eventName,
      local_guid: getOrCreateLocalGuid(),
      timestamp: new Date().toISOString(),
      lang: locale.toLowerCase(),
      page_path: location.pathname,
    },
  });
}

/**
 * Push a custom event to GTM dataLayer.
 * Every push includes local_guid + timestamp so GA always receives them.
 * After pushing, stale payload keys are reset to undefined to prevent
 * them leaking into the next unrelated event.
 */
export function sendToGtmDataLayer(eventName, payload = {}) {
  if (typeof window === 'undefined' || !gtmInitialized) return;

  const eventData = {
    event: eventName,
    local_guid: getOrCreateLocalGuid(),
    timestamp: new Date().toISOString(),
    ...payload,
  };

  try {
    TagManager.dataLayer({ dataLayer: eventData });
  } catch (error) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(eventData);
  }

  // Clean up payload-specific keys so they don't bleed into the next event
  const cleanUp = Object.keys(payload).reduce((acc, key) => {
    acc[key] = undefined;
    return acc;
  }, {});
  if (Object.keys(cleanUp).length) {
    TagManager.dataLayer({ dataLayer: cleanUp });
  }
}
